/**
 * Primitivas del cifrado de aplicación (cliente), con Web Crypto nativa: sin librerías.
 * Formato idéntico al del gateway (common/crypto/envelope.ts): AES-256-GCM, `ct` = texto cifrado || etiqueta de 16 bytes, IV de 12 bytes.
 */
export interface Envelope {
  v: number;
  iv: string;
  ct: string;
  /** 1 = el gateway comprimió el JSON (gzip) antes de cifrarlo. */
  z?: number;
}

export const ENVELOPE_VERSION = 1;

const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);
const buf = (u: Uint8Array): BufferSource => u as unknown as BufferSource;

export function toB64(bytes: ArrayBuffer | Uint8Array): string {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode(...u.subarray(i, i + 0x8000));
  return btoa(s);
}

export function fromB64(b64: string): Uint8Array {
  const s = atob(b64);
  const u = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i);
  return u;
}

export const randomB64Url = (bytes: number): string =>
  toB64(crypto.getRandomValues(new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const isEnvelope = (x: unknown): x is Envelope =>
  !!x && typeof x === 'object' && (x as Envelope).v === ENVELOPE_VERSION && typeof (x as Envelope).iv === 'string' && typeof (x as Envelope).ct === 'string';

/** Liga la petición a su método, ruta, sesión, marca de tiempo y nonce (igual que `requestAad` del gateway). */
export const requestAad = (method: string, path: string, sid: string, ts: string, nonce: string): Uint8Array =>
  utf8(`v1\nREQ\n${method.toUpperCase()}\n${path}\n${sid}\n${ts}\n${nonce}`);

/** La respuesta queda ligada al nonce de su petición y a su código de estado. */
export const responseAad = (nonce: string, status: number): Uint8Array => utf8(`v1\nRES\n${nonce}\n${status}`);

export interface SessionKeys {
  sid: string;
  /** El gateway anunció que sabe comprimir antes de cifrar (un gateway sin esa capacidad rechazaría la cabecera `x-enc-z`). */
  z?: boolean;
  /** Cliente → servidor. */
  c2s: CryptoKey;
  /** Servidor → cliente. */
  s2c: CryptoKey;
}

/** Envuelve el secreto de sesión con la clave pública RSA-OAEP-SHA256 del gateway. */
export async function wrapSecret(publicKeySpkiB64: string, secret: Uint8Array): Promise<string> {
  const pub = await crypto.subtle.importKey('spki', buf(fromB64(publicKeySpkiB64)), { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt']);
  return toB64(await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, pub, buf(secret)));
}

/** Deriva las dos claves de dirección (HKDF-SHA256; la sal es el id de sesión), igual que `deriveSessionKeys` del gateway. */
export async function deriveKeys(secret: Uint8Array, sid: string): Promise<SessionKeys> {
  const base = await crypto.subtle.importKey('raw', buf(secret), 'HKDF', false, ['deriveKey']);
  const derive = (info: string) =>
    crypto.subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: buf(utf8(sid)), info: buf(utf8(info)) }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  return { sid, c2s: await derive('faisen-enc-v1 c2s'), s2c: await derive('faisen-enc-v1 s2c') };
}

export async function encryptBytes(key: CryptoKey, plain: Uint8Array, aad: Uint8Array): Promise<Envelope> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: buf(iv), additionalData: buf(aad) }, key, buf(plain));
  return { v: ENVELOPE_VERSION, iv: toB64(iv), ct: toB64(ct) };
}

/** Lanza si el mensaje fue alterado o no corresponde a esta petición. */
export async function decryptJson(key: CryptoKey, env: Envelope, aad: Uint8Array): Promise<unknown> {
  // La marca `z` forma parte de los datos autenticados: si alguien la altera, el descifrado falla.
  const aadUsed = env.z === 1 ? new Uint8Array([...aad, ...utf8('\nz1')]) : aad;
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buf(fromB64(env.iv)), additionalData: buf(aadUsed) }, key, buf(fromB64(env.ct)));
  const bytes = env.z === 1 ? await gunzip(new Uint8Array(plain)) : plain;
  return JSON.parse(new TextDecoder().decode(bytes));
}

/** Navegadores que pueden descomprimir gzip: solo ellos piden respuestas comprimidas (`x-enc-z`). */
export const canInflate = (): boolean => typeof DecompressionStream !== 'undefined';

async function gunzip(data: Uint8Array): Promise<ArrayBuffer> {
  return new Response(new Blob([buf(data)]).stream().pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
}

export const encodeJson = (value: unknown): Uint8Array => utf8(JSON.stringify(value));
